import { useState, useEffect } from "react";

/**
 * Hook to determine the number of grid columns based on viewport width
 * Matches the Tailwind breakpoints used in ImageGrid component:
 * - default (mobile): 2 columns
 * - md (768px+): 3 columns
 * - lg (1024px+): 4 columns
 * - 2xl (1536px+): 5 columns
 */
export function useGridColumns() {
  const [columns, setColumns] = useState(() => {
    if (typeof window === "undefined") return 4; // Default for SSR

    const width = window.innerWidth;
    if (width >= 1536) return 5; // 2xl
    if (width >= 1024) return 4; // lg
    if (width >= 768) return 3; // md
    return 2; // default
  });

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      let newColumns: number;

      if (width >= 1536) {
        newColumns = 5; // 2xl
      } else if (width >= 1024) {
        newColumns = 4; // lg
      } else if (width >= 768) {
        newColumns = 3; // md
      } else {
        newColumns = 2; // default
      }

      setColumns(newColumns);
    };

    // Update on mount
    updateColumns();

    // Add resize listener with debounce
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateColumns, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return columns;
}

/**
 * Calculate the number of items to fetch to ensure complete rows
 * @param columns - Number of columns in the grid
 * @param rowsToLoad - Number of complete rows to load (default 4)
 * @returns Number of items to fetch
 */
export function calculateGridLimit(columns: number, rowsToLoad = 4): number {
  return columns * rowsToLoad;
}

/**
 * Calculate how many additional items are needed to complete the current row
 * @param currentCount - Current number of items
 * @param columns - Number of columns in the grid
 * @returns Number of additional items needed
 */
export function calculateItemsToCompleteRow(currentCount: number, columns: number): number {
  const remainder = currentCount % columns;
  return remainder === 0 ? 0 : columns - remainder;
}
